import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Chips } from "@/components/form";
import { SortablePhotos } from "@/components/SortablePhotos";
import { PhotoEditSheet, RoomSelect } from "@/components/PhotoEdit";

export const photoSrc = (url: string) => `${process.env.REACT_APP_BACKEND_URL}${url}`;
const KIND: Record<string, string> = { room: "Room", common: "Common area" };

export function PhotosManager({ propertyId }: { propertyId: string }) {
  const { data, reload, setData } = useApi<any[]>(`/properties/${propertyId}/photos`);
  const rooms = useApi<any[]>(`/properties/${propertyId}/rooms`);
  const [roomId, setRoomId] = useState("");
  const [editing, setEditing] = useState<any>(null);
  const roomName = (id?: string) => (rooms.data || []).find((r) => r.id === id)?.name;
  const labelOf = (p: any) => p.caption || (p.room_id && roomName(p.room_id) ? `Room ${roomName(p.room_id)}` : KIND[p.kind]);
  const ref = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState("room");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async (files: FileList) => {
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        fd.append("caption", caption);
        if (kind === "room" && roomId) fd.append("room_id", roomId);
        await api.post(`/properties/${propertyId}/photos`, fd);
      }
      setCaption("");
      toast.success("Photos added");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
      reload();
    }
  };
  const remove = async (id: string) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/properties/${propertyId}/photos/${id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reorder = async (ids: string[]) => {
    const prev = data || [];
    setData(ids.map((id) => prev.find((p) => p.id === id)));
    try {
      const r = await api.put(`/properties/${propertyId}/photos/order`, { ids });
      setData(r.data);
    } catch (e) {
      setData(prev);
      toast.error(errMsg(e));
    }
  };
  const photos = data || [];
  return (
    <div className="space-y-3" data-testid="photos-manager">
      <p className="text-sm font-semibold text-slate-700">Photos ({photos.length}/12)</p>
      {photos.length > 1 && <p className="text-xs text-slate-500">Tap a photo to edit it. Drag to reorder (press and hold on phone). The first photo is the cover residents see in search.</p>}
      {photos.length > 0 && (
        <SortablePhotos photos={photos} srcOf={(p) => photoSrc(p.url)} labelOf={labelOf} onReorder={reorder} onDelete={remove} onEdit={setEditing} />
      )}
      <Chips testId="photo-kind" value={kind} onChange={setKind} options={[{ value: "room", label: "Room" }, { value: "common", label: "Common area" }]} />
      {kind === "room" && <RoomSelect rooms={rooms.data || []} value={roomId} onChange={setRoomId} testId="photo-room-select" />}
      <input data-testid="photo-caption-input" value={caption} maxLength={80} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional), e.g. Room 101 - 2 sharing"
        className="w-full h-12 rounded-xl border border-slate-200 bg-white px-3 outline-none focus:ring-2 focus:ring-emerald-600" />
      <p className="text-[11px] text-slate-400 text-right -mt-2" data-testid="photo-caption-count">{caption.length}/80</p>
      <input ref={ref} data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => e.target.files?.length && upload(e.target.files)} />
      <button type="button" data-testid="photo-upload-btn" disabled={busy || photos.length >= 12} onClick={() => ref.current?.click()}
        className="w-full h-12 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 font-semibold flex items-center justify-center gap-2 pg-tap disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}Add photos
      </button>
      <PhotoEditSheet propertyId={propertyId} photo={editing} rooms={rooms.data || []} src={editing ? photoSrc(editing.url) : ""} onClose={() => setEditing(null)}
        onSaved={(p) => setData((data || []).map((x) => (x.id === p.id ? p : x)))} />
    </div>
  );
}

export function PhotoGallery({ photos }: { photos: any[] }) {
  if (!photos?.length) return null;
  return (
    <div className="-mx-4 px-4 flex gap-2 overflow-x-auto snap-x snap-mandatory pb-1" data-testid="listing-gallery">
      {photos.map((p) => (
        <figure key={p.id} className="snap-start shrink-0 w-[85%] aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 relative">
          <img src={photoSrc(p.url)} alt={p.caption || KIND[p.kind]} className="h-full w-full object-cover" loading="lazy" />
          <figcaption className="absolute bottom-2 left-2 text-xs font-semibold bg-black/55 text-white rounded-full px-2.5 py-1">{p.caption || KIND[p.kind]}</figcaption>
        </figure>
      ))}
    </div>
  );
}
